import * as yup from 'yup';
import { fieldValidationMessages } from 'app/constants';
import { emptyStringToNull } from 'app/utils/utils';
import {
  AddIpiData,
  Ipi,
  IpiValidations,
  UpdateIpiData,
} from 'types/ipi.types';
import { SocietyOption } from '../types';
import { IpiForm } from './types';
import { affiliationToSocietyOption } from '../utils';
import {
  manualRequiredIdNameValidation,
  oneOfRequiredIdNameValidation,
} from '../validations';

const ipiTransform = (value: number, originalValue: string) =>
  originalValue === '' ? null : value;

const ipiNameNumberValidation = (validations?: IpiValidations) =>
  yup.number().when('society', {
    is: (option?: SocietyOption) => !option?.verification,
    then: yup
      .number()
      .transform(ipiTransform)
      .required(fieldValidationMessages.REQUIRED)
      .min(validations?.properties?.ipiNameNumber?.minimum || 1, 'Invalid IPI')
      .max(
        validations?.properties?.ipiNameNumber?.maximum || 99999999999,
        'Invalid IPI'
      )
      .typeError('Invalid IPI')
      .nullable(),
    otherwise: yup.number().transform(ipiTransform).nullable(),
  });

const getDefaultAddIpiValidationSchema = () => {
  const defaultMaxLength = 255;
  return yup.object().shape(
    {
      society: yup.object().typeError(fieldValidationMessages.REQUIRED),
      ipiNameNumber: ipiNameNumberValidation(),
      firstName: manualRequiredIdNameValidation(defaultMaxLength),
      lastName: manualRequiredIdNameValidation(defaultMaxLength),
    },
    [['firstName', 'lastName']]
  );
};

export const getAddIpiValidationSchema = (validations?: IpiValidations) => {
  if (!validations) {
    return getDefaultAddIpiValidationSchema();
  }
  const anyOf = validations?.anyOf.map((el) => el.required).flat();
  const { firstName, lastName } = validations.properties;
  const firstNameMax = firstName.maxLength;
  const lastNameMax = lastName.maxLength;

  return yup.object().shape(
    {
      society: yup.object().typeError(fieldValidationMessages.REQUIRED),
      ipiNameNumber: ipiNameNumberValidation(validations),
      ...(anyOf.includes('firstName') && anyOf.includes('lastName')
        ? {
            firstName: oneOfRequiredIdNameValidation('lastName', firstNameMax),
            lastName: oneOfRequiredIdNameValidation('firstName', lastNameMax),
          }
        : {
            firstName: manualRequiredIdNameValidation(firstNameMax),
            lastName: manualRequiredIdNameValidation(lastNameMax),
          }),
    },
    [['firstName', 'lastName']]
  );
};

export const getUpdateIpiValidationSchema = (validations?: IpiValidations) => {
  const { firstName, lastName } = validations?.properties || {};
  const defaultMaxLength = 255;
  const firstNameMax = firstName?.maxLength || defaultMaxLength;
  const lastNameMax = lastName?.maxLength || defaultMaxLength;

  return yup.object().shape(
    {
      society: yup.object().typeError(fieldValidationMessages.REQUIRED),
      ipiNameNumber: ipiNameNumberValidation(validations),
      firstName: oneOfRequiredIdNameValidation('lastName', firstNameMax),
      lastName: oneOfRequiredIdNameValidation('firstName', lastNameMax),
    },
    [['firstName', 'lastName']]
  );
};

export const getDefaultValues = (ipi?: Ipi): IpiForm => ({
  society: ipi?.prSocietyAffiliation
    ? affiliationToSocietyOption(ipi.prSocietyAffiliation)
    : null,
  firstName: ipi?.firstName || '',
  lastName: ipi?.lastName || '',
  ipiNameNumber: ipi?.ipiNameNumber.toString() || '',
  isDefault: ipi?.default ?? false,
});

export const toAddIpiData = ({
  society,
  ipiNameNumber,
  ...rest
}: Partial<IpiForm>): AddIpiData => ({
  prSocietyAffiliation: society?.id || '',
  ipiNameNumber: Number(ipiNameNumber),
  ...rest,
});

export const toUpdateIpiData = ({
  firstName,
  lastName,
}: Partial<IpiForm>): Partial<UpdateIpiData> => ({
  firstName: emptyStringToNull(firstName),
  lastName: emptyStringToNull(lastName),
});
